/**
 * Detalhamento de um cenário.
 *
 * Mostra a distribuição de latência e os percentis. O histograma existe para
 * tornar a **cauda à direita** visível: é ela que separa mediana de percentil 95,
 * e é essa separação que justifica a regra de nunca apresentar média.
 */

import { useMemo } from "react";
import { Button } from "@cloudflare/kumo";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { HistogramChart } from "../components/charts.js";
import { DataTable, type Column } from "../components/DataTable.js";
import { DefinitionList } from "../components/DefinitionList.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { VerdictBadge } from "../components/VerdictBadge.js";
import { snapshot } from "../data/source.js";
import { TARGET_TOKENS_PER_SEC } from "../fixtures/scale.js";
import { formatContext, formatNumber } from "../lib/format.js";
import { buildHistogram } from "../lib/histogram.js";

/** Cenário aberto quando a rota chega sem identificador legível. */
const DEFAULT_SCENARIO_ID = 3;

/** Latência por token que corresponde ao alvo de vazão, em milissegundos. */
const TARGET_TPOT_MS = 1_000 / TARGET_TOKENS_PER_SEC;

/** Uma linha da tabela de percentis. */
interface PercentileRow {
  readonly metric: string;
  readonly p50: number;
  readonly p95: number;
  readonly p99: number;
}

/** Colunas de percentis; o p95 em destaque, porque é ele que o alvo cobra. */
const PERCENTILE_COLUMNS: readonly Column<PercentileRow>[] = [
  { key: "metric", title: "", render: (row) => <span className="font-medium">{row.metric}</span> },
  { key: "p50", title: "p50", align: "right", render: (row) => formatNumber(row.p50, 1) },
  {
    key: "p95",
    title: "p95",
    align: "right",
    render: (row) => <span className="font-semibold text-kumo-strong">{formatNumber(row.p95, 1)}</span>,
  },
  { key: "p99", title: "p99", align: "right", render: (row) => formatNumber(row.p99, 1) },
];

/** Detalhe de um cenário, com distribuição de latência e percentis. */
export function ScenarioDetail(): React.ReactElement {
  const params = useParams();
  const navigate = useNavigate();
  const parsedId = Number(params["id"]);
  const scenarioId = Number.isFinite(parsedId) ? parsedId : DEFAULT_SCENARIO_ID;

  const scenario = snapshot.scenarios.find((s) => s.id === scenarioId);
  const stats = snapshot.stats.find((s) => s.scenarioId === scenarioId);
  const run = snapshot.runs.find((r) => r.id === scenario?.runId);
  const drops = snapshot.drops.filter((d) => d.scenarioId === scenarioId);

  /**
   * Histograma de latência por token, em faixas coloridas conforme o alvo.
   *
   * Só entram respostas geradas pelo motor: resposta servida do cache de saída tem
   * latência de outra ordem de grandeza e achataria a distribuição que interessa.
   */
  const histogram = useMemo(() => {
    const values = snapshot.samples
      .filter((s) => s.scenarioId === scenarioId && s.ok && s.servedFrom === "engine")
      .map((s) => s.tpotMs);
    return buildHistogram(values, TARGET_TPOT_MS);
  }, [scenarioId]);

  const backButton = (
    <div>
      <Button variant="ghost" size="sm" icon={ArrowLeftIcon} onClick={() => navigate("/runs")}>
        Execuções
      </Button>
    </div>
  );

  if (scenario === undefined || stats === undefined) {
    return (
      <Page>
        {backButton}
        <Notice tone="error" title={`Cenário ${scenarioId} não encontrado`} />
      </Page>
    );
  }

  const percentiles: readonly PercentileRow[] = [
    { metric: "latência por token (ms)", p50: stats.tpotP50Ms, p95: stats.tpotP95Ms, p99: stats.tpotP99Ms },
    { metric: "tempo até o 1º token (ms)", p50: stats.ttftP50Ms, p95: stats.ttftP95Ms, p99: stats.ttftP99Ms },
  ];

  return (
    <Page>
      {backButton}

      {/* A ressalva vem ANTES do número: quem lê o selo primeiro já decidiu. */}
      {run?.comparable === false && (
        <Notice tone="warning" title="Execução NÃO COMPARÁVEL" description={run.notComparableReason} />
      )}

      <Panel bodyClassName="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="flex justify-center lg:justify-start">
          <VerdictBadge verdict={stats.verdict} size="large" />
        </div>
        <div className="flex-1">
          <DefinitionList
            columns={4}
            items={[
              { label: "instância", value: run?.instanceType ?? "—" },
              { label: "aceleradores", value: `${run?.gpuCount ?? 0}× ${run?.gpuModel ?? "—"}` },
              { label: "motor", value: run?.engine ?? "—" },
              { label: "topologia", value: run?.topology ?? "—" },
              { label: "perfil", value: scenario.loadProfile },
              { label: "concorrência", value: scenario.concurrency },
              { label: "contexto", value: formatContext(scenario.contextLen) },
              { label: "cache de prefixo", value: scenario.radixCache ? "ligado" : "desligado" },
            ]}
          />
        </div>
      </Panel>

      {drops.length > 0 && (
        <Notice
          tone="info"
          title={`${formatNumber(drops[0]?.dropped ?? 0)} amostras descartadas — ${drops[0]?.reason ?? ""}`}
        />
      )}

      <div className="grid gap-5 xl:grid-cols-3">
        <Panel
          title="Distribuição da latência por token"
          description="A cauda à direita é o que separa a mediana do percentil 95 — e é por ela que média nunca aparece como métrica principal nesta ferramenta."
          className="xl:col-span-2"
        >
          <HistogramChart
            bars={histogram}
            xTitle={`ms por token — alvo em ${formatNumber(TARGET_TPOT_MS)} ms`}
            yTitle="requisições"
          />
        </Panel>
        <Panel title="Percentis" bodyClassName="flex flex-col gap-5">
          <DataTable columns={PERCENTILE_COLUMNS} rows={percentiles} rowKey={(row) => row.metric} />
          <div className="grid grid-cols-2 gap-5">
            <Stat label="tok/s (p95)" value={stats.tokensPerSecP95} precision={1} size="compact" />
            <Stat label="goodput" value={stats.goodputPct} precision={1} suffix="%" size="compact" />
          </div>
          <p className="text-sm text-kumo-subtle">
            {formatNumber(stats.sampleCount - stats.okCount)} de {formatNumber(stats.sampleCount)} requisições falharam
          </p>
        </Panel>
      </div>
    </Page>
  );
}
